import { Stack, Typography, useTheme } from "@mui/material";
import {
	type Control,
	type UseFormSetValue,
	useController,
} from "react-hook-form";
import {
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientPropertyCarVehicleTypeEnum,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	CarExtendedParams,
	type DisclosurePlacement,
} from "web_myplann/Portfolio/Forms/CarForm/CarExtendedParams";
import {
	CAR_EXTENDED_FIELDS,
	CAR_REQUIRED_MINIMUM_FIELDS,
	type CarFormInput,
	type CarFormOutput,
} from "web_myplann/Portfolio/Forms/CarForm/carFormSchema";
import { CAR_SECURITY_TYPES } from "web_myplann/Portfolio/Helpers/carSecurityTypes";
import { CAR_SPECIAL_EQUIPMENT } from "web_myplann/Portfolio/Helpers/carSpecialEquipment";
import { useBooleanGroupOptions } from "web_myplann/Portfolio/Hooks/useBooleanGroupOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSwitch";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { FlatRowSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { UNITS } from "web_myplann/Shared/Utils/units";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";

const PageResources = Resources.Portfolio.AddCar;
const PropertyResources = Resources.PortfolioDetail.Property;

type CarFormFieldsProps = {
	control: Control<CarFormInput, unknown, CarFormOutput>;
	setValue: UseFormSetValue<CarFormInput>;
	disclosurePlacement: DisclosurePlacement;
};

export const CarFormFields = ({
	control,
	setValue,
	disclosurePlacement,
}: CarFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const today = new Date();

	const fuelOptions = useEnumSelectOptions(
		ClientCarFuelEnum,
		PropertyResources.CarFuelTypeValue,
		[ClientCarFuelEnum.None],
	);

	const vehicleTypeOptions = useEnumSelectOptions(
		ClientPropertyVehicleTypeEnum,
		PropertyResources.CarPropertyTypeValue,
		[ClientPropertyVehicleTypeEnum.None],
	);

	const carVehicleTypeOptions = useEnumSelectOptions(
		ClientPropertyCarVehicleTypeEnum,
		PropertyResources.CarVehicleTypeValue,
		[
			ClientPropertyCarVehicleTypeEnum.None,
			ClientPropertyCarVehicleTypeEnum.Empty,
		],
	);

	const bodyTypeOptions = useEnumSelectOptions(
		ClientCarBodyTypeEnum,
		PropertyResources.CarBodyTypeValue,
		[ClientCarBodyTypeEnum.None, ClientCarBodyTypeEnum.DEPRECATED_EMPTY],
	);

	const {
		field: propertyTypeField,
		fieldState: { error: propertyTypeError },
	} = useController({ control, name: "propertyType" });

	const isOtherElectro =
		propertyTypeField.value === ClientPropertyVehicleTypeEnum.OtherElectro;

	const changePropertyType = (propertyType: ClientPropertyVehicleTypeEnum) => {
		propertyTypeField.onChange(propertyType);
		if (propertyType === ClientPropertyVehicleTypeEnum.OtherElectro) {
			return;
		}
		const dirtyOptions = { shouldDirty: true };
		setValue("carVehicleType", null, dirtyOptions);
		setValue("carSerialNumber", "", dirtyOptions);
		setValue("carMaxSpeed", null, dirtyOptions);
	};

	const securityOptions = useBooleanGroupOptions(CAR_SECURITY_TYPES);
	const specialEquipmentOptions = useBooleanGroupOptions(CAR_SPECIAL_EQUIPMENT);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowSelect
					label={t(PropertyResources.CarPropertyType)}
					options={vehicleTypeOptions}
					value={propertyTypeField.value}
					onChange={changePropertyType}
					onBlur={propertyTypeField.onBlur}
					placeholder={t(PageResources.Placeholders.select)}
					error={!!propertyTypeError?.message}
					helperText={propertyTypeError?.message}
				/>
				{isOtherElectro && (
					<FlatRowFormSelect
						control={control}
						name="carVehicleType"
						label={t(PropertyResources.CarVehicleType)}
						options={carVehicleTypeOptions}
						placeholder={t(PageResources.Placeholders.select)}
					/>
				)}
				<FlatRowFormInput
					control={control}
					name="carBrand"
					label={t(PageResources.Fields.brand)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="carModel"
					label={t(PageResources.Fields.model)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="value"
					type="float"
					label={t(PageResources.Fields.currentValue)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix={UNITS.crown}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="carManufactureMonthYear"
					label={t(PageResources.Fields.manufactureMonthYear)}
					views={[DateView.Year, DateView.Month]}
					displayFormat={DateFormat.MonthYear}
					maxDate={today}
					placeholder={t(PageResources.Placeholders.select)}
				/>
				<FlatRowFormInput
					control={control}
					name="carLicensePlate"
					label={t(PageResources.Fields.licensePlate)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="carVin"
					label={t(PropertyResources.CarVin)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="carPurchaseDate"
					label={t(PropertyResources.CarPurchaseDate)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(PageResources.Placeholders.select)}
				/>

				<CarExtendedParams
					control={control}
					fields={CAR_EXTENDED_FIELDS}
					disclosurePlacement={disclosurePlacement}
				>
					<FlatRowFormSelect
						control={control}
						name="carFuel"
						label={t(PropertyResources.CarFuelType)}
						options={fuelOptions}
						placeholder={t(PageResources.Placeholders.select)}
					/>
					<FlatRowFormInput
						control={control}
						name="carPower"
						type="number"
						max={ValidationLimits.Amount.MaxPower}
						label={t(PropertyResources.CarPower)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix={UNITS.kilowatt}
					/>
					<FlatRowFormInput
						control={control}
						name="carVolume"
						type="number"
						max={ValidationLimits.Amount.MaxVolume}
						label={t(PropertyResources.CarVolume)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix={UNITS.cubicCentimeter}
					/>
					<FlatRowFormSelect
						control={control}
						name="carBodyType"
						label={t(PropertyResources.CarBodyType)}
						options={bodyTypeOptions}
						placeholder={t(PageResources.Placeholders.select)}
					/>
					<FlatRowFormInput
						control={control}
						name="carWeight"
						type="number"
						max={ValidationLimits.Amount.MaxWeight}
						label={t(PropertyResources.CarWeight)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix={UNITS.kilogram}
					/>
					<FlatRowFormInput
						control={control}
						name="carSeatCount"
						type="number"
						label={t(PropertyResources.CarSeatCount)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						max={ValidationLimits.Amount.MaxSeatCount}
					/>
					<FlatRowFormDatePicker
						control={control}
						name="carRegistration"
						label={t(PropertyResources.CarRegistration)}
						views={[DateView.Year, DateView.Month, DateView.Day]}
						displayFormat={DateFormat.Display}
						maxDate={today}
						placeholder={t(PageResources.Placeholders.select)}
					/>
					<FlatRowFormDatePicker
						control={control}
						name="carPutIntoService"
						label={t(PropertyResources.CarPutIntoService)}
						views={[DateView.Year, DateView.Month, DateView.Day]}
						displayFormat={DateFormat.Display}
						placeholder={t(PageResources.Placeholders.select)}
					/>
					{isOtherElectro && (
						<>
							<FlatRowFormInput
								control={control}
								name="carSerialNumber"
								label={t(PropertyResources.CarSerialNumber)}
								placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
							/>
							<FlatRowFormInput
								control={control}
								name="carMaxSpeed"
								type="float"
								label={t(PropertyResources.CarMaxSpeed)}
								placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
								suffix={UNITS.kilometerPerHour}
							/>
						</>
					)}
					<FlatRowFormSelect
						control={control}
						name="carSecurity"
						label={t(PropertyResources.CarSecurity)}
						options={securityOptions}
						placeholder={t(PageResources.Placeholders.select)}
						multiple
					/>
					<FlatRowFormSelect
						control={control}
						name="carSpecialEquipment"
						label={t(PropertyResources.CarSpecialEquipment)}
						options={specialEquipmentOptions}
						placeholder={t(PageResources.Placeholders.select)}
						multiple
						isLast
					/>
				</CarExtendedParams>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CAR_REQUIRED_MINIMUM_FIELDS}
				message={t(PageResources.Validations.requiredMinimum)}
			/>

			<Typography variant="sectionLabel">
				{t(PageResources.Sections.classification)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="forInvestments"
					label={t(Resources.Portfolio.AddArt.Fields.forInvestments)}
					isLast
				/>
			</Stack>

			<Typography variant="sectionLabel">
				{t(PageResources.Sections.description)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="description"
					label={t(PageResources.Fields.description)}
					placeholder={t(PageResources.descriptionPlaceholder)}
					rows={1}
					isLast
				/>
			</Stack>
		</>
	);
};
